/**
 * =============================================================================
 * WORKFLOW NODE - INDIVIDUAL WORKFLOW STEP CARD
 * =============================================================================
 * 
 * PURPOSE:
 * This component renders a SINGLE WORKFLOW STEP in the canvas.
 * It displays the step's icon, title, subtitle, and provides click interaction.
 * 
 * DRAG-AND-DROP:
 * Uses @dnd-kit/sortable for smooth reordering within WorkflowStack.
 * Trigger steps are disabled from sorting (always remain at position 0).
 * 
 * VISUAL DISPLACEMENT (LIVE NODE MOVEMENT):
 * - Visual-only. Does not affect drop logic.
 * - useSortable() provides transform/transition for smooth animations.
 * - When another item is dragged, this node shifts to preview drop position.
 * - The transform comes from @dnd-kit's internal collision detection.
 * - This is purely visual - actual reorder happens on drop.
 * 
 * PLACEHOLDER NODE:
 * - Visual-only. Does not affect drop logic.
 * - When step.type === 'placeholder', renders a dashed empty box.
 * - This temporary node appears when dragging NEW tools from the sidebar.
 * - It enables live preview by participating in SortableContext.
 * - The placeholder is NEVER persisted or passed to addNode().
 * 
 * PROPS SCHEMA:
 * @param {Object} step - The step data object
 * @param {string} step.id - Unique identifier (used by @dnd-kit)
 * @param {string} step.type - 'trigger' | 'email' | 'sms' | 'wait' | 'placeholder'
 * @param {string} step.title - Display title
 * @param {string} step.subtitle - Description text
 * @param {Component} step.icon - Lucide icon component
 * @param {boolean} isFirst - Whether this is the first step (affects border-radius)
 * @param {boolean} isLast - Whether this is the last step (affects border-radius)
 * @param {boolean} isSelected - Whether this step is currently selected
 * @param {Function} onClick - Click handler (opens config panel)
 * @param {boolean} isMobile - Whether to render mobile layout
 * =============================================================================
 */
import React, { useState, useRef, useEffect } from 'react';
import { GripVertical, MoreHorizontal, Zap, Mail, MessageSquare, Clock, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DRAG_TYPES } from '../constants/dragTypes';

/**
 * Icon mapping by step type
 * Firestore steps don't have icon components, so we derive them from type
 */
const STEP_ICONS = {
  trigger: Zap,
  email: Mail,
  sms: MessageSquare,
  wait: Clock,
  webhook: Zap,
};

/**
 * Generates the trigger subtitle from config
 * @param {Object} config - The trigger config object
 * @param {Array} forms - Available forms for resolving formId to name
 * @returns {Object} { description: string, highlight: string }
 */
const getTriggerDescription = (config, forms = []) => {
  if (!config) {
    return { description: 'No trigger configured', highlight: null };
  }

  const entryType = config.entryType || 'form';

  if (entryType === 'form') {
    const formId = config.formId;
    if (!formId) {
      return { description: 'When', highlight: 'Form Submitted', suffix: '(No form selected)' };
    }
    const form = forms.find(f => f.id === formId);
    const formName = form?.name || form?.propertyName || 'Unknown Form';
    return { description: 'When', highlight: 'Form Submitted', suffix: `on ${formName}` };
  }

  if (entryType === 'manual') {
    // resolvedLeadCount now reflects ONLY explicitly selected leads (not filter matches)
    const leadCount = config.selectedLeadIds?.length || config.resolvedLeadCount || 0;
    if (leadCount > 0) {
      return { description: 'Manually add', highlight: `${leadCount} lead${leadCount !== 1 ? 's' : ''}`, suffix: null };
    }
    // Check if filters are applied
    const hasFilters = (config.leadFilters?.tags?.length > 0) ||
      (config.leadFilters?.status && config.leadFilters?.status !== 'All');
    if (hasFilters) {
      return { description: 'Manually add', highlight: 'leads', suffix: '(filters applied)' };
    }
    return { description: 'Manually add', highlight: 'leads', suffix: '(none selected)' };
  }

  return { description: 'Unknown trigger type', highlight: null };
};

const WorkflowNode = ({
  step,
  isFirst,
  isLast,
  isSelected,
  onClick,
  isMobile,
  forms = [],
  automationStatus = 'DRAFT',
  hasSteps = false,
  onRemoveStep,
  isReadOnly = false,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMenu]);
  // Get icon from step's icon prop OR derive from type (Firestore steps don't have icon)
  const Icon = step.icon || STEP_ICONS[step.type?.toLowerCase()] || Zap;

  // Safe subtitle renderer: prevents objects from being passed as React children
  const renderSubtitle = (subtitle) => {
    if (subtitle === null || subtitle === undefined) return '';
    if (typeof subtitle === 'string' || typeof subtitle === 'number') return subtitle;
    if (typeof subtitle === 'object') return subtitle.type || subtitle.name || JSON.stringify(subtitle);
    return String(subtitle);
  };

  // =============================================================================
  // @dnd-kit SORTABLE HOOK
  // =============================================================================
  // useSortable provides everything needed for drag-and-drop reordering:
  // - setNodeRef: Ref to attach to the draggable DOM element
  // - attributes: ARIA attributes for accessibility
  // - listeners: Event handlers for drag interactions
  // - transform: Current drag/displacement transform (x, y offset)
  // - transition: CSS transition for smooth animations
  // - isDragging: Whether this item is currently being dragged
  //
  // VISUAL DISPLACEMENT (LIVE NODE MOVEMENT):
  // - Visual-only. Does not affect drop logic.
  // - When dragging occurs, @dnd-kit computes transforms for ALL nodes.
  // - Non-dragged nodes receive transforms to shift out of the way.
  // - This creates the "live preview" effect where nodes move smoothly.
  // - The actual reorder only happens on drop (in AutomationBuilder).
  //
  // disabled: Trigger steps cannot be moved (always index 0)
  // =============================================================================
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: step.id,
    disabled: step.type === 'trigger' || isReadOnly, // Trigger cannot be reordered; read-only disables all sorting
    data: {
      type: DRAG_TYPES.STEP, // Identifies this as an existing workflow step
    },
  });

  // =============================================================================
  // TRANSFORM STYLES - Visual-only displacement
  // =============================================================================
  // Visual-only. Does not affect drop logic.
  // 
  // - CSS.Transform.toString: Converts @dnd-kit transform to CSS string
  // - transition: Smooth 200ms ease animation for displacement
  // - When dragging: Higher z-index, reduced opacity, shadow effect
  // - When displaced: Smooth shift via transform animation
  // =============================================================================
  const style = {
    // Transform for drag position OR displacement preview
    transform: CSS.Transform.toString(transform),
    // Smooth transition for displacement animation (200ms ease)
    // This makes non-dragged nodes shift smoothly
    transition: transition || 'transform 200ms ease',
    // Visual feedback when THIS node is being dragged
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
    // Subtle shadow when dragging to show elevation
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : undefined,
  };

  // =============================================================================
  // PLACEHOLDER RENDERING - Visual-only temporary node
  // =============================================================================
  // Visual-only. Does not affect drop logic.
  // 
  // When dragging a NEW tool from the sidebar, a placeholder node is inserted
  // into the list to enable live preview. This renders as a simple dashed box
  // that matches the height of a normal node, allowing other nodes to shift
  // around it naturally via @dnd-kit's displacement system.
  // 
  // The placeholder is NEVER persisted or passed to addNode().
  // =============================================================================
  if (step.type === 'placeholder') {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`stack-node p-4 border-2 border-dashed border-blue-300 bg-blue-50/50 ${isFirst ? 'rounded-t-xl' : '-mt-px'
          } ${isLast ? 'rounded-b-xl' : ''}`}
      >
        {/* Empty placeholder - just takes up space for visual preview */}
        <div className="h-6 flex items-center justify-center">
          <span className="text-xs text-blue-400 font-medium">Drop here</span>
        </div>
      </div>
    );
  }

  // ========== MOBILE LAYOUT ==========
  if (isMobile) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`stack-node p-4 bg-white border border-notion-border ${isFirst ? 'rounded-t-xl' : '-mt-px'} ${isLast ? 'rounded-b-xl' : ''} ${isSelected ? 'bg-orange-50 border-orange-200 z-20' : ''}`}
      >
        <div className="flex gap-3">
          {/* 
            GRIP HANDLE - Drag handle for reordering
            - listeners attached here so only grip initiates drag
            - Trigger steps don't show grip (cannot be moved)
          */}
          {step.type !== 'trigger' && !isReadOnly && (
            <div
              className="mt-1 text-stone-300 cursor-grab active:cursor-grabbing touch-none"
              {...listeners}
              {...attributes}
            >
              <GripVertical className="w-4 h-4" />
            </div>
          )}
          {/* Clickable content area - opens ConfigPanel */}
          <div
            className={`flex-1 ${step.type === 'trigger' ? '' : 'overflow-hidden'}`}
            onClick={onClick}
          >
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                {step.type === 'trigger' ? (
                  <div className="w-10 h-10 rounded-lg bg-stone-100 border border-stone-200 flex items-center justify-center text-stone-600">
                    <Icon className="w-5 h-5" />
                  </div>
                ) : (
                  <Icon className={`w-3.5 h-3.5 ${step.type === 'wait' ? 'text-orange-400' : 'text-stone-500'}`} />
                )}
                <div>
                  <span className={`${step.type === 'trigger' ? 'text-xs font-bold text-stone-800 uppercase tracking-wide' : 'text-sm font-bold text-stone-700'} block`}>
                    {step.title}
                  </span>
                  {step.type === 'trigger' ? (
                    <span className="text-[10px] text-stone-500">{renderSubtitle(step.subtitle)}</span>
                  ) : (
                    <p className="text-xs text-stone-500 truncate">{renderSubtitle(step.subtitle)}</p>
                  )}
                </div>
              </div>
              {step.type === 'trigger' ? (
                <div className="px-1.5 py-0.5 bg-stone-100 rounded text-[9px] text-stone-500 font-medium">Entry</div>
              ) : !isReadOnly ? (
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                    className="p-1 rounded hover:bg-stone-100 transition-colors"
                  >
                    <MoreHorizontal className="w-4 h-4 text-stone-300" />
                  </button>
                  {showMenu && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg py-1 z-50 min-w-[140px]">
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowMenu(false); onRemoveStep?.(step.id); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete Step
                      </button>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== DESKTOP LAYOUT ==========
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`stack-node p-4 bg-white border border-notion-border ${isReadOnly ? 'cursor-default' : 'cursor-pointer hover:bg-stone-50'} transition ${isFirst ? 'rounded-t-xl' : '-mt-px'} ${isLast ? 'rounded-b-xl' : ''} ${isSelected ? 'bg-orange-50 border-orange-200 z-20' : ''}`}
    >
      <div className="flex gap-4">
        {/* 
          GRIP HANDLE - Drag handle for reordering
          - @dnd-kit listeners attached ONLY to grip handle
          - This allows clicking elsewhere on the node without triggering drag
          - touch-none prevents touch scrolling from interfering with drag
        */}
        {step.type !== 'trigger' && !isReadOnly && (
          <div
            className="mt-1 cursor-grab active:cursor-grabbing text-stone-300 hover:text-stone-500 touch-none"
            {...listeners}
            {...attributes}
          >
            <GripVertical className="w-4 h-4" />
          </div>
        )}
        {/* Clickable content area - opens ConfigPanel */}
        <div className="flex-1" onClick={onClick}>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              {step.type === 'trigger' ? (
                <div className="w-10 h-10 rounded-lg bg-stone-100 border border-stone-200 flex items-center justify-center text-stone-600">
                  <Icon className="w-5 h-5" />
                </div>
              ) : null}
              <div>
                {step.type === 'trigger' ? (
                  (() => {
                    const triggerInfo = getTriggerDescription(step.config, forms);
                    return (
                      <>
                        <span className="text-xs font-bold text-stone-800 uppercase tracking-wide block">Start Trigger</span>
                        <span className="text-xs text-stone-500">
                          {triggerInfo.description}{' '}
                          {triggerInfo.highlight && <strong>{triggerInfo.highlight}</strong>}
                          {triggerInfo.suffix && ` ${triggerInfo.suffix}`}
                        </span>
                      </>
                    );
                  })()
                ) : (
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-3.5 h-3.5 ${step.type === 'wait' ? 'text-orange-400' : 'text-stone-500'}`} />
                      <span className="text-sm font-bold text-stone-700">{step.title}</span>
                    </div>
                  </div>
                )}
                {step.type !== 'trigger' && (
                  <p className="text-xs text-stone-500 line-clamp-1">{renderSubtitle(step.subtitle)}</p>
                )}
              </div>
            </div>
            {step.type === 'trigger' ? (
              <div className="flex items-center gap-2">
                {/* Status Badges */}
                {automationStatus === 'ACTIVE' && (
                  <div className="px-2 py-1 bg-emerald-100 rounded text-[10px] text-emerald-700 font-medium">Active</div>
                )}
                {automationStatus === 'PAUSED' && (
                  <div className="px-2 py-1 bg-orange-100 rounded text-[10px] text-orange-700 font-medium">Paused</div>
                )}
                {automationStatus === 'STOPPED' && (
                  <div className="px-2 py-1 bg-red-100 rounded text-[10px] text-red-700 font-medium">Stopped</div>
                )}
                {automationStatus === 'DRAFT' && (
                  <div className="px-2 py-1 bg-stone-100 rounded text-[10px] text-stone-500 font-medium">Entry Point</div>
                )}
              </div>
            ) : !isReadOnly ? (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                  className="p-1 rounded hover:bg-stone-100 transition-colors"
                >
                  <MoreHorizontal className="w-4 h-4 text-stone-300 hover:text-stone-500" />
                </button>
                {showMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg py-1 z-50 min-w-[140px]">
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowMenu(false); onRemoveStep?.(step.id); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete Step
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowNode;
